import moment from 'moment';
import type { Content, TDocumentDefinitions } from 'pdfmake/interfaces';

import { DATE_TIME_FORMAT } from '@/config';
import {
  ICFS,
  IKase,
  IOffenseOptionItem,
  IUser,
  MasterNameType,
} from '@/models';
import { IAgency } from '@/models/agency';
import { buildReportHeader } from '@/utils/reports/buildReportHeader';

import {
  IKasePrintFields,
  IKasePrintModalForm,
  KasePrintSections,
} from '../data';
import { getCachedBase64Image } from './imageHelpers';
import { pdfStyles, pageMargins } from './pdfStyles';
import {
  renderPDFField,
  createTable,
  createCard,
  createCardHeader,
  createDetailGrid,
  createDividerLine,
} from './transformDataForPDF';

interface BuildPDFDefinitionParams {
  agencyData: IAgency | null;
  kase: IKase | null;
  activeCFSData: ICFS | undefined;
  user: IUser | null;
  formData: IKasePrintModalForm;
  allModalData: Record<string, (Record<string, any> | null | undefined)[]>;
  kasePrintFieldsRecord: Record<string, Record<number, IKasePrintFields[]>>;
  isEmail: boolean;
  PropertyType?: Map<string, IOffenseOptionItem>;
  PropertyInvolvementList?: Map<string, IOffenseOptionItem>;
  logoBase64?: string;
}

/**
 * Build the complete PDF definition for pdfmake
 */
export const buildPDFDefinition = async (
  params: BuildPDFDefinitionParams,
): Promise<TDocumentDefinitions> => {
  const {
    agencyData,
    kase,
    activeCFSData,
    user,
    formData,
    allModalData,
    kasePrintFieldsRecord,
    isEmail,
    PropertyType,
    PropertyInvolvementList,
    logoBase64,
  } = params;

  // Convert logo to base64 if available
  let logo = logoBase64;
  if (!logo && agencyData?.logo?.url) {
    try {
      logo = await getCachedBase64Image(agencyData.logo.url);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load logo:', error);
    }
  }

  // Build header function (only used when printHeaderOnAllPages is enabled)
  const header = formData.printHeaderOnAllPages
    ? (): Content => buildHeaderContent()
    : undefined;

  // Build header content
  const buildHeaderContent = (): Content => {
    // Report-specific entries column - Case info (rebuilt per page — pdfMake mutates header nodes)
    const reportEntries: Content[] = [
      {
        text: `Case ID: ${kase?.number || ''}`,
        style: 'headerCaseInfo',
        margin: [0, 0, 0, 2] as [number, number, number, number],
      },
    ];

    if (activeCFSData?.number) {
      reportEntries.push({
        text: `CFS ID: ${activeCFSData.number}`,
        style: 'headerCaseInfo',
        margin: [0, 0, 0, 2] as [number, number, number, number],
      });
    }

    reportEntries.push({
      text: `${isEmail ? 'Emailed' : 'Printed'} on: ${moment().format(
        DATE_TIME_FORMAT,
      )}`,
      style: 'headerTimestamp',
      margin: [0, 0, 0, 0] as [number, number, number, number],
    });

    reportEntries.push({
      text: `${isEmail ? 'Emailed' : 'Printed'} by: ${user?.fullName || ''}`,
      style: 'headerTimestamp',
      margin: [0, 0, 0, 0] as [number, number, number, number],
    });

    return buildReportHeader({
      agency: agencyData,
      logo,
      reportEntries,
      outerMargin: [20, 10, 20, 30],
    });
  };

  // Build footer function
  const footer = (currentPage: number, pageCount: number): Content => {
    return {
      text: `Page ${currentPage} of ${pageCount}`,
      alignment: 'center',
      style: 'footer',
      margin: [0, 10, 0, 0] as [number, number, number, number],
    };
  };

  // Build content sections, filtering out empty ones
  const sections: Content[] = [
    // When not repeating header, include it as the first content section (with reduced margin since page margins apply)
    ...(!formData.printHeaderOnAllPages
      ? [
          Object.assign(buildHeaderContent(), {
            margin: [0, 0, 0, 10] as [number, number, number, number],
          }),
        ]
      : []),
    buildCaseOverviewSection(),
    buildDispatchInfoSection(),
    buildOfficersSection(),
    buildOffensesSection(),
    buildNamesSection(),
    buildVehiclesSection(),
    buildNarrativesSection(),
    buildPropertySection(),
    ...(isEmail ? [buildEmailDetailsSection()] : []),
    buildAuditDetailsSection(),
    buildSignatureSection(),
  ];

  const content = sections.filter((section) => {
    if (!section) return false;
    if (typeof section === 'object' && 'text' in section && section.text === '')
      return false;
    return true;
  });

  // Helper function to build sections
  function buildCaseOverviewSection(): Content {
    const data = allModalData?.[KasePrintSections.CaseOverview]?.[0];
    const fields =
      kasePrintFieldsRecord?.[KasePrintSections.CaseOverview]?.[0] || [];

    if (!data || fields.length === 0) return { text: '' };

    const fieldData = fields.map((field) => ({
      label: field.label,
      value: renderPDFField(
        field,
        data,
        KasePrintSections.CaseOverview,
        PropertyType,
        PropertyInvolvementList,
      ),
    }));

    return createCard(
      'MAIN CASE INFORMATION',
      createDetailGrid(fieldData, 3),
      true,
    );
  }

  function buildDispatchInfoSection(): Content {
    const data = allModalData?.[KasePrintSections.DispatchInfo]?.[0];
    const fields =
      kasePrintFieldsRecord?.[KasePrintSections.DispatchInfo]?.[0] || [];

    if (!data || fields.length === 0) return { text: '' };

    // Title fields
    const titleFields = ['number', 'start', 'end', 'incidentCodes'];
    const titleFieldsData = titleFields
      .map((fieldValue) => fields.find((f) => f.value === fieldValue))
      .filter((field): field is IKasePrintFields => field !== undefined)
      .map((field) => ({
        label: field.label,
        value: renderPDFField(
          field,
          data,
          KasePrintSections.DispatchInfo,
          PropertyType,
          PropertyInvolvementList,
        ),
      }));

    const detailFields = [
      'addressInfo',
      'shortDescription',
      'wasReadyToDispatchAt',
      'cfsHistoryLogs',
      'cfsHistoryLogs',
      'cfsHistoryLogs',
      'cfsHistoryLogs',
    ];

    // Use the same approach as getFieldsWithIndices to handle duplicate field values
    const detailData: Array<{ label: string; value: Content }> = [];
    const usedIndices = new Set<number>();

    detailFields.forEach((fieldValue) => {
      const fieldIndex = fields.findIndex(
        (f, idx) => f.value === fieldValue && !usedIndices.has(idx),
      );

      if (fieldIndex !== -1) {
        const field = fields[fieldIndex];
        usedIndices.add(fieldIndex);
        detailData.push({
          label: field.label,
          value: renderPDFField(
            field,
            data,
            KasePrintSections.DispatchInfo,
            PropertyType,
            PropertyInvolvementList,
          ),
        });
      }
    });

    return createCard(
      'DISPATCH INFORMATION',
      {
        stack: [
          titleFieldsData.length > 0
            ? createCardHeader(titleFieldsData)
            : { text: '' },
          titleFieldsData.length > 0 ? createDividerLine() : { text: '' },
          createDetailGrid(detailData, 3),
        ],
      },
      true,
    );
  }

  function buildOfficersSection(): Content {
    const data = allModalData?.[KasePrintSections.Officers] as Record<
      string,
      any
    >[];
    const fields =
      kasePrintFieldsRecord?.[KasePrintSections.Officers]?.[0] || [];

    if (!data || data.length === 0 || fields.length === 0) return { text: '' };

    const headers = fields.map((f) => f.label);
    const rows = data.map((officer, idx) => {
      const rowFields =
        kasePrintFieldsRecord?.[KasePrintSections.Officers]?.[idx] || fields;
      return rowFields.map((field) => {
        // Return the full Content object to preserve redaction styling
        return renderPDFField(
          field,
          officer,
          KasePrintSections.Officers,
          PropertyType,
          PropertyInvolvementList,
        );
      });
    });

    return createCard('OFFICERS INVOLVED', createTable(headers, rows), true);
  }

  function buildOffensesSection(): Content {
    const data = allModalData?.[KasePrintSections.OffenseInfo] as Record<
      string,
      any
    >[];
    const fields =
      kasePrintFieldsRecord?.[KasePrintSections.OffenseInfo]?.[0] || [];

    if (!data || data.length === 0 || fields.length === 0) return { text: '' };

    const headers = fields.map((f) => f.label);
    const rows = data.map((offense, idx) => {
      const rowFields =
        kasePrintFieldsRecord?.[KasePrintSections.OffenseInfo]?.[idx] || fields;
      return rowFields.map((field) => {
        // Return the full Content object to preserve redaction styling
        return renderPDFField(
          field,
          offense,
          KasePrintSections.OffenseInfo,
          PropertyType,
          PropertyInvolvementList,
        );
      });
    });

    return createCard('OFFENSES', createTable(headers, rows), true);
  }

  function buildNamesSection(): Content {
    const data = allModalData?.[KasePrintSections.NamesList] as Record<
      string,
      any
    >[];

    if (!data || data.length === 0) return { text: '' };

    // Section title in bordered box, centered
    const titleBox = {
      table: {
        widths: ['*'],
        body: [
          [
            {
              text: 'NAMES',
              style: 'sectionTitle',
              alignment: 'center' as const,
            },
          ],
        ],
      },
      layout: {
        hLineWidth: () => 1,
        vLineWidth: () => 1,
        hLineColor: () => '#000',
        vLineColor: () => '#000',
        paddingLeft: () => 5,
        paddingRight: () => 5,
        paddingTop: () => 3,
        paddingBottom: () => 3,
      },
      margin: [0, 0, 0, 3] as [number, number, number, number],
    };

    const cards: Content[] = [titleBox];

    data.forEach((item, idx) => {
      const fields =
        kasePrintFieldsRecord?.[KasePrintSections.NamesList]?.[idx] || [];

      const titleFields = ['name', 'involvement'];
      // Transient persons have no current address: drop the empty Sub address
      // and City/State/ZIP fields (the first 'addresses' still renders "Transient").
      const detailFields =
        item?.type === MasterNameType.ORGANIZATION
          ? [
              'addresses',
              'addresses',
              'cityStateZip',
              'organizationType',
              'phone',
              'businessPhone',
              'mobilePhone',
              'type',
            ]
          : item?.transient
          ? ['sex', 'race', 'birthday', 'age', 'addresses', 'phone', 'type']
          : [
              'sex',
              'race',
              'birthday',
              'age',
              'addresses',
              'addresses',
              'cityStateZip',
              'phone',
              'type',
            ];

      const titleData = fields
        .filter((f) => titleFields.includes(f.value))
        .map((field) => ({
          label: field.label,
          value: renderPDFField(
            field,
            item,
            KasePrintSections.NamesList,
            PropertyType,
            PropertyInvolvementList,
          ),
        }));

      // Use the same approach as getFieldsWithIndices to handle duplicate field values (addresses)
      const detailData: Array<{ label: string; value: Content }> = [];
      const usedIndices = new Set<number>();

      detailFields.forEach((fieldValue) => {
        const fieldIndex = fields.findIndex(
          (f, fieldIdx) => f.value === fieldValue && !usedIndices.has(fieldIdx),
        );

        if (fieldIndex !== -1) {
          const field = fields[fieldIndex];
          usedIndices.add(fieldIndex);
          detailData.push({
            label: field.label,
            value: renderPDFField(
              field,
              item,
              KasePrintSections.NamesList,
              PropertyType,
              PropertyInvolvementList,
            ),
          });
        }
      });

      // Wrap each card in a table for border
      cards.push({
        table: {
          widths: ['*'],
          body: [
            [
              {
                stack: [
                  titleData.length > 0
                    ? createCardHeader(titleData)
                    : { text: '' },
                  titleData.length > 0 ? createDividerLine() : { text: '' },
                  createDetailGrid(detailData, 3),
                ],
              },
            ],
          ],
        },
        layout: {
          hLineWidth: () => 1,
          vLineWidth: () => 1,
          hLineColor: () => '#000',
          vLineColor: () => '#000',
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 0,
        },
        margin: [0, 0, 0, 3] as [number, number, number, number],
      });
    });

    return {
      stack: cards,
      margin: [0, 0, 0, 10] as [number, number, number, number],
    };
  }

  function buildVehiclesSection(): Content {
    const data = allModalData?.[KasePrintSections.VehiclesList] as Record<
      string,
      any
    >[];

    if (!data || data.length === 0) return { text: '' };

    // Section title in bordered box, centered
    const titleBox = {
      table: {
        widths: ['*'],
        body: [
          [
            {
              text: 'VEHICLES',
              style: 'sectionTitle',
              alignment: 'center' as const,
            },
          ],
        ],
      },
      layout: {
        hLineWidth: () => 1,
        vLineWidth: () => 1,
        hLineColor: () => '#000',
        vLineColor: () => '#000',
        paddingLeft: () => 5,
        paddingRight: () => 5,
        paddingTop: () => 3,
        paddingBottom: () => 3,
      },
      margin: [0, 0, 0, 3] as [number, number, number, number],
    };

    const cards: Content[] = [titleBox];

    data.forEach((item, idx) => {
      const fields =
        kasePrintFieldsRecord?.[KasePrintSections.VehiclesList]?.[idx] || [];

      const titleFields = ['make', 'model'];
      const detailFields = ['year', 'color', 'owner', 'licensePlate'];

      const titleData = fields
        .filter((f) => titleFields.includes(f.value))
        .map((field) => ({
          label: field.label,
          value: renderPDFField(
            field,
            item,
            KasePrintSections.VehiclesList,
            PropertyType,
            PropertyInvolvementList,
          ),
        }));

      const detailData = fields
        .filter((f) => detailFields.includes(f.value))
        .map((field) => ({
          label: field.label,
          value: renderPDFField(
            field,
            item,
            KasePrintSections.VehiclesList,
            PropertyType,
            PropertyInvolvementList,
          ),
        }));

      // Wrap each card in a table for border
      cards.push({
        table: {
          widths: ['*'],
          body: [
            [
              {
                stack: [
                  titleData.length > 0
                    ? createCardHeader(titleData)
                    : { text: '' },
                  titleData.length > 0 ? createDividerLine() : { text: '' },
                  createDetailGrid(detailData, 3),
                ],
              },
            ],
          ],
        },
        layout: {
          hLineWidth: () => 1,
          vLineWidth: () => 1,
          hLineColor: () => '#000',
          vLineColor: () => '#000',
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 0,
        },
        margin: [0, 0, 0, 3] as [number, number, number, number],
      });
    });

    return {
      stack: cards,
      margin: [0, 0, 0, 10] as [number, number, number, number],
    };
  }

  function buildNarrativesSection(): Content {
    const data = allModalData?.[KasePrintSections.NarrativesList] as Record<
      string,
      any
    >[];

    if (!data || data.length === 0) return { text: '' };

    // Section title in bordered box, centered
    const titleBox = {
      table: {
        widths: ['*'],
        body: [
          [
            {
              text: 'NARRATIVES',
              style: 'sectionTitle',
              alignment: 'center' as const,
            },
          ],
        ],
      },
      layout: {
        hLineWidth: () => 1,
        vLineWidth: () => 1,
        hLineColor: () => '#000',
        vLineColor: () => '#000',
        paddingLeft: () => 5,
        paddingRight: () => 5,
        paddingTop: () => 3,
        paddingBottom: () => 3,
      },
      margin: [0, 0, 0, 3] as [number, number, number, number],
    };

    const cards: Content[] = [titleBox];

    data.forEach((item, idx) => {
      const fields =
        kasePrintFieldsRecord?.[KasePrintSections.NarrativesList]?.[idx] || [];

      const contentField = fields.find((f) => f.value === 'content');
      const footerFields = ['createdBy', 'createdAt', 'type'];

      const narrativeContent = contentField
        ? renderPDFField(
            contentField,
            item,
            KasePrintSections.NarrativesList,
            PropertyType,
            PropertyInvolvementList,
          )
        : { text: ' ' };

      // Build footer with proper redaction styling
      const footerParts: any[] = [];
      const footerFieldsData = fields.filter((f) =>
        footerFields.includes(f.value),
      );

      footerFieldsData.forEach((field, index) => {
        const rendered = renderPDFField(
          field,
          item,
          KasePrintSections.NarrativesList,
          PropertyType,
          PropertyInvolvementList,
        );

        // Add label for type field
        if (field.value === 'type') {
          footerParts.push({ text: 'Type: ', style: 'fieldValue', bold: true });
        }

        // Add the rendered content (preserves redaction styling)
        if (typeof rendered === 'object' && 'text' in rendered) {
          footerParts.push(rendered);
        } else {
          footerParts.push({ text: rendered || ' ', style: 'fieldValue' });
        }

        // Add separator between fields (but not after the last one)
        if (index < footerFieldsData.length - 1) {
          footerParts.push({ text: ' • ', style: 'fieldValue' });
        }
      });

      // Apply margin to narrativeContent
      const contentWithMargin =
        typeof narrativeContent === 'object' && narrativeContent !== null
          ? {
              ...narrativeContent,
              margin: [5, 3, 5, 3] as [number, number, number, number],
            }
          : {
              text: narrativeContent,
              margin: [5, 3, 5, 3] as [number, number, number, number],
            };

      // Wrap each narrative card in a table for border
      cards.push({
        table: {
          widths: ['*'],
          body: [
            [
              {
                stack: [
                  contentWithMargin,
                  createDividerLine(),
                  {
                    text: footerParts,
                    style: 'footer',
                    margin: [5, 3, 5, 3] as [number, number, number, number],
                  },
                ],
              },
            ],
          ],
        },
        layout: {
          hLineWidth: () => 1,
          vLineWidth: () => 1,
          hLineColor: () => '#000',
          vLineColor: () => '#000',
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 0,
        },
        margin: [0, 0, 0, 3] as [number, number, number, number],
      });
    });

    return {
      stack: cards,
      margin: [0, 0, 0, 10] as [number, number, number, number],
    };
  }

  function buildPropertySection(): Content {
    const data = allModalData?.[KasePrintSections.PropertyList] as Record<
      string,
      any
    >[];

    if (!data || data.length === 0) return { text: '' };

    // Section title in bordered box, centered
    const titleBox = {
      table: {
        widths: ['*'],
        body: [
          [
            {
              text: 'PROPERTY INVOLVED',
              style: 'sectionTitle',
              alignment: 'center' as const,
            },
          ],
        ],
      },
      layout: {
        hLineWidth: () => 1,
        vLineWidth: () => 1,
        hLineColor: () => '#000',
        vLineColor: () => '#000',
        paddingLeft: () => 5,
        paddingRight: () => 5,
        paddingTop: () => 3,
        paddingBottom: () => 3,
      },
      margin: [0, 0, 0, 3] as [number, number, number, number],
    };

    const cards: Content[] = [titleBox];

    data.forEach((item, idx) => {
      const fields =
        kasePrintFieldsRecord?.[KasePrintSections.PropertyList]?.[idx] || [];

      const titleFields = ['number', 'type', 'involvement', 'value'];
      const detailFields = [
        'typeTag',
        'name',
        'serialNumber',
        'owner',
        'status',
        'loggedInfo',
        'droppedOffLocation',
        'year',
        'make',
        'model',
        'color',
        'licensePlate',
      ];

      const titleData = titleFields
        .map((fieldValue) => fields.find((f) => f.value === fieldValue))
        .filter((field): field is IKasePrintFields => field !== undefined)
        .map((field) => ({
          label: field.label,
          value: renderPDFField(
            field,
            item,
            KasePrintSections.PropertyList,
            PropertyType,
            PropertyInvolvementList,
          ),
        }));

      const detailData = detailFields
        .map((fieldValue) => fields.find((f) => f.value === fieldValue))
        .filter((field): field is IKasePrintFields => field !== undefined)
        .map((field) => ({
          label: field.label,
          value: renderPDFField(
            field,
            item,
            KasePrintSections.PropertyList,
            PropertyType,
            PropertyInvolvementList,
          ),
        }));

      // Wrap each card in a table for border
      cards.push({
        table: {
          widths: ['*'],
          body: [
            [
              {
                stack: [
                  titleData.length > 0
                    ? createCardHeader(titleData)
                    : { text: '' },
                  titleData.length > 0 ? createDividerLine() : { text: '' },
                  createDetailGrid(detailData, 3),
                ],
              },
            ],
          ],
        },
        layout: {
          hLineWidth: () => 1,
          vLineWidth: () => 1,
          hLineColor: () => '#000',
          vLineColor: () => '#000',
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 0,
        },
        margin: [0, 0, 0, 3] as [number, number, number, number],
      });
    });

    return {
      stack: cards,
      margin: [0, 0, 0, 10] as [number, number, number, number],
    };
  }

  function buildEmailDetailsSection(): Content {
    const data = allModalData?.[KasePrintSections.EmailDetails]?.[0];
    const fields =
      kasePrintFieldsRecord?.[KasePrintSections.EmailDetails]?.[0] || [];

    if (!data || fields.length === 0) return { text: '' };

    const fieldData = fields.map((field) => ({
      label: field.label,
      value: renderPDFField(
        field,
        data,
        KasePrintSections.EmailDetails,
        PropertyType,
        PropertyInvolvementList,
      ),
    }));

    return createCard('EMAIL DETAILS', createDetailGrid(fieldData, 3), true);
  }

  function buildAuditDetailsSection(): Content {
    const data = allModalData?.[KasePrintSections.AuditDetails]?.[0];
    const fields =
      kasePrintFieldsRecord?.[KasePrintSections.AuditDetails]?.[0] || [];

    if (!data || fields.length === 0) return { text: '' };

    const fieldData = fields.map((field) => ({
      label: field.label,
      value: renderPDFField(
        field,
        data,
        KasePrintSections.AuditDetails,
        PropertyType,
        PropertyInvolvementList,
      ),
    }));

    return createCard('AUDIT DETAILS', createDetailGrid(fieldData, 3), true);
  }

  function buildSignatureSection(): Content {
    // A4 width is 595.28 points, minus left and right margins (20 + 20 = 40)
    const contentWidth = 555.28;

    return {
      stack: [
        {
          canvas: [
            {
              type: 'line',
              x1: 0,
              y1: 0,
              x2: contentWidth,
              y2: 0,
              lineWidth: 1,
              lineColor: '#000',
            },
          ],
          margin: [0, 0, 0, 5] as [number, number, number, number],
        },
        {
          text: 'Primary Officer:',
          style: 'footer',
          bold: true,
          margin: [0, 0, 0, 25] as [number, number, number, number],
        },
        {
          canvas: [
            {
              type: 'line',
              x1: 0,
              y1: 0,
              x2: contentWidth,
              y2: 0,
              lineWidth: 1,
              lineColor: '#000',
            },
          ],
          margin: [0, 0, 0, 5] as [number, number, number, number],
        },
        {
          text: 'Supervisor Approving:',
          style: 'footer',
          bold: true,
          margin: [0, 0, 0, 25] as [number, number, number, number],
        },
        {
          canvas: [
            {
              type: 'line',
              x1: 0,
              y1: 0,
              x2: contentWidth,
              y2: 0,
              lineWidth: 1,
              lineColor: '#000',
            },
          ],
          margin: [0, 0, 0, 5] as [number, number, number, number],
        },
      ],
      margin: [0, 10, 0, 0] as [number, number, number, number],
    };
  }

  // Return the complete PDF definition
  return {
    ...(header ? { header } : {}),
    footer,
    content,
    styles: pdfStyles,
    pageMargins: formData.printHeaderOnAllPages
      ? pageMargins
      : [20, 30, 20, 30],
    pageSize: 'A4',
    pageOrientation: 'portrait',
    defaultStyle: {
      fontSize: 10,
      color: '#000',
    },
  };
};
